import EventEmitter from 'eventemitter3';
import { FuseRenderer } from '@/renderer';

/**
 * Passthrough, always. The range is played over a real room: the tee is placed
 * against a real ball on a real mat, the window is framed by real walls, and the
 * anchor that holds all of it still is a feature of `immersive-ar` alone.
 *
 * There is deliberately no `immersive-vr` path. A VR session has no room to
 * hit-test, anchor or frame against, so every one of those flows would need a
 * second implementation that guesses instead of measuring — and this build is
 * launch-monitor golf, where the real ball is the one thing that must be
 * registered accurately.
 */
const XR_MODE = 'immersive-ar';

/** Optional features worth asking for; missing ones degrade quietly. */
const OPTIONAL_FEATURES = ['hit-test', 'anchors', 'hand-tracking', 'plane-detection'];

export type FuseXRSessionOptions = {
  renderer: FuseRenderer;
  /** XR framebuffer scale. Must be set before the session starts. */
  framebufferScale?: number;
  /** Fixed foveation 0 (none) to 1 (max). Applied live after session start. */
  foveation?: number;
  /**
   * Preferred display refresh rate, Hz. The closest rate the device supports
   * is chosen. Default: the highest offered. A rate the app can HOLD beats a
   * higher one it misses — passthrough shares the compositor, so app frame
   * drops read as passthrough lag.
   */
  targetFrameRate?: number;
  requiredFeatures?: string[];
  optionalFeatures?: string[];
};

interface EventMap {
  sessionstart: () => void;
  sessionend: () => void;
  /** The reference space was reset (e.g. Meta-button recenter). */
  referencereset: () => void;
}

/**
 * Lifecycle wrapper for an immersive-ar (passthrough) WebXR session.
 * Keeps session management out of FuseRenderer and reusable across apps.
 */
export class FuseXRSession extends EventEmitter<EventMap> {
  session?: XRSession;
  referenceSpace?: XRReferenceSpace;

  #renderer: FuseRenderer;
  #options: FuseXRSessionOptions;

  constructor(options: FuseXRSessionOptions) {
    super();
    this.#renderer = options.renderer;
    this.#options = options;
  }

  static async isSupported(): Promise<boolean> {
    if (!navigator.xr) return false;
    try {
      return await navigator.xr.isSessionSupported(XR_MODE);
    } catch {
      return false;
    }
  }

  get isPresenting(): boolean {
    return this.#xr?.isPresenting ?? false;
  }

  get #xr() {
    return this.#renderer.xr;
  }

  /** Whether the granted session includes a feature (hit-test, anchors, …). */
  hasFeature(name: string): boolean {
    return !!(this.session as any)?.enabledFeatures?.includes(name);
  }

  /** Must be called from a user-gesture handler (e.g. an Enter MR button). */
  async start(): Promise<XRSession> {
    if (!navigator.xr) {
      throw new Error('WebXR is not available in this browser');
    }
    if (this.session) return this.session;

    const xr = this.#xr;
    xr.setFramebufferScaleFactor(this.#options.framebufferScale ?? 1.0);
    xr.setReferenceSpaceType('local-floor');

    // Only local-floor is truly required — hit-test/anchors have graceful
    // fallbacks downstream, and requiring them would reject the session
    // outright on runtimes that lack them.
    const session = await navigator.xr.requestSession(XR_MODE, {
      requiredFeatures: this.#options.requiredFeatures ?? ['local-floor'],
      optionalFeatures: this.#options.optionalFeatures ?? OPTIONAL_FEATURES,
    });

    try {
      await xr.setSession(session);
    } catch (error) {
      // Never keep a session that three failed to attach — a poisoned
      // this.session would short-circuit every future start()
      try { await session.end(); } catch { /* already dead */ }
      throw error;
    }
    this.session = session;

    // Registered AFTER setSession so three's own teardown listener (added
    // inside setSession) runs first and xr state is clean when we emit
    session.addEventListener('end', () => {
      this.session = undefined;
      this.referenceSpace = undefined;
      this.#renderer.setXRExposure(false);
      this.emit('sessionend');
    });

    xr.setFoveation(this.#options.foveation ?? 0.4);

    // Prefer the highest refresh rate the device offers (Quest defaults 72 Hz),
    // or the closest supported rate to an explicit target
    try {
      const rates = (session as any).supportedFrameRates as Float32Array | undefined;
      if (rates?.length && (session as any).updateTargetFrameRate) {
        const wanted = this.#options.targetFrameRate;
        const rate = wanted === undefined
          ? Math.max(...rates)
          : [...rates].reduce((best, r) => Math.abs(r - wanted) < Math.abs(best - wanted) ? r : best);
        await (session as any).updateTargetFrameRate(rate);
        console.log(`XR refresh rate: ${rate} Hz (supported: ${[...rates].join(', ')})`);
      }
    } catch (error) {
      console.warn('Could not raise target frame rate', error);
    }

    this.referenceSpace = xr.getReferenceSpace() ?? undefined;
    // Meta-button recenter fires 'reset' — re-read the space and let the app
    // decide whether the world needs re-placing
    this.referenceSpace?.addEventListener?.('reset', () => {
      this.referenceSpace = xr.getReferenceSpace() ?? this.referenceSpace;
      this.emit('referencereset');
    });

    this.#renderer.setXRExposure(true);
    this.emit('sessionstart');
    return session;
  }

  async end(): Promise<void> {
    if (this.session) {
      await this.session.end();
    }
  }
}
